import { createContext, useContext, useMemo, useState } from "react";

import Service from "../api/service";
import { _TYPES_ } from "../pages/doseeData/utils/types";
import { useAdminContext } from "./admin/AdminContext";

/* ────────────────────────────────────────────────────────────────────────────
 * Entity registry
 *
 * Every DoSee "thing" (Event, Hotel, Safari, …) looks the same from this
 * context's point of view: a list + a count + a REST route. Defining them
 * here once means we never repeat this knowledge in a switch/if chain
 * — fetch/create/update/delete/status all read from the same list.
 *
 * To add a new entity, add one row here and nothing else.
 * ──────────────────────────────────────────────────────────────────────── */
const ENTITIES = [
  { type: _TYPES_.EVENT,         route: "event",          dataKey: "events",         countKey: "eventsCount" },
  { type: _TYPES_.NEWS,          route: "news",           dataKey: "news",           countKey: "newsCount" },
  { type: _TYPES_.MUSIC,         route: "music",          dataKey: "music",          countKey: "musicCount" },
  { type: _TYPES_.SAFARI,        route: "safari",         dataKey: "safari",         countKey: "safariCount" },
  { type: _TYPES_.CINEMA,        route: "cinema",         dataKey: "cinema",         countKey: "cinemaCount" },
  { type: _TYPES_.ATTRACTION,    route: "attraction",     dataKey: "attractions",    countKey: "attractionsCount" },
  { type: _TYPES_.RESTAURANT,    route: "restaurant",     dataKey: "restaurant",     countKey: "restaurantCount" },
  { type: _TYPES_.BESTOFAFRICA,  route: "best/africa",    dataKey: "bestOfAfrica",   countKey: "bestOfAfricaCount" },
  { type: _TYPES_.THINGSTODO,    route: "things/todo",    dataKey: "thingsToDos",    countKey: "thingsToDosCount" },
  { type: _TYPES_.PARTY,         route: "party",          dataKey: "parties",        countKey: "partiesCount" },
  { type: _TYPES_.NIGHTLIFE,     route: "nightlife",      dataKey: "nightlifes",     countKey: "nightlifesCount" },
  { type: _TYPES_.CITYGUIDE,     route: "city/guide",     dataKey: "cityGuides",     countKey: "cityGuidesCount" },
  { type: _TYPES_.HOTEL,         route: "hotel",          dataKey: "hotels",         countKey: "hotelsCount" },
  { type: _TYPES_.CLASSES,       route: "class",          dataKey: "classes",        countKey: "classesCount" },
  { type: _TYPES_.SHOPPING,      route: "shopping",       dataKey: "shoppings",      countKey: "shoppingsCount" },
  { type: _TYPES_.FOOD,          route: "food",           dataKey: "foods",          countKey: "foodsCount" },
  { type: _TYPES_.BOOK,          route: "book",           dataKey: "books",          countKey: "booksCount" },
  { type: _TYPES_.NEIGHBORHOOD,  route: "neighborhood",   dataKey: "neighborhoods",  countKey: "neighborhoodsCount" },
  { type: _TYPES_.MINISTRY,      route: "ministry",       dataKey: "ministries",     countKey: "ministriesCount" },
  { type: _TYPES_.GETTINGAROUND, route: "getting/around", dataKey: "gettingArounds", countKey: "gettingAroundsCount" },
  { type: _TYPES_.ART,           route: "art",            dataKey: "arts",           countKey: "artsCount" },
  { type: _TYPES_.MOVIE,         route: "movie",          dataKey: "movies",         countKey: "moviesCount" },
  { type: _TYPES_.DANCE,         route: "dance",          dataKey: "dances",         countKey: "dancesCount" },
  { type: _TYPES_.THEATER,       route: "theater",        dataKey: "theaters",       countKey: "theatersCount" },
  { type: _TYPES_.TIPSANDADVICE, route: "tips/advice",    dataKey: "tipsAndAdvice",  countKey: "tipsAndAdviceCount" },
  { type: _TYPES_.MUSEUM,        route: "museum",         dataKey: "museums",        countKey: "museumsCount" },
  { type: _TYPES_.ADV,           route: "sponsorship",    dataKey: "advertisements", countKey: "advertisementCount" },
  { type: _TYPES_.TOURPACKAGE,   route: "tour/package",   dataKey: "tourPackages",   countKey: "tourPackagesCount" },
  { type: _TYPES_.PRODUCT,       route: "product",        dataKey: "products",       countKey: "productsCount" },
  { type: _TYPES_.COMPANY,       route: "companies",      dataKey: "companies",      countKey: "companiesCount" },
  { type: _TYPES_.EMBASSY,       route: "embassy",        dataKey: "embassies",      countKey: "embassiesCount" },
];

const BY_TYPE = Object.fromEntries(ENTITIES.map((e) => [e.type, e]));

const routeFor = (content) => BY_TYPE[content]?.route ?? "event";

const pickPositionKeys = (position = {}) => {
  const keys = ["country_id", "first_level_id", "second_level_id", "third_level_id", "fourth_level_id"];
  const picked = {};
  for (const k of keys) if (position[k]) picked[k] = position[k];
  return picked;
};

/* ─── Context ───────────────────────────────────────────────────────────── */

const DoSeeDataContext = createContext(null);

export const DoSeeDataProvider = ({ children }) => {
  // Single source of truth: one object keyed by _TYPES_, replacing 66 useStates.
  const [entries, setEntries] = useState({});
  const [sponsorships, setSponsorships] = useState(null);

  const { sysAdmin, singleAdmin } = useAdminContext();

  const setEntry = (type, patch) =>
    setEntries((prev) => ({ ...prev, [type]: { ...(prev[type] ?? {}), ...patch } }));

  /* ── API actions ─────────────────────────────────────────────────────── */

  const fetchDoSeeData = async (args, page = 1, pageSize = 50) => {
    const {
      country_id,
      first_level_id,
      second_level_id,
      third_level_id,
      fourth_level_id,
      content,
    } = args;

    const { ids, type } = singleAdmin ?? {};
    const year = singleAdmin?.year;
    const month = singleAdmin?.month;
    const noLocation = singleAdmin?.noLocation;

    const positionKeys =
      country_id === 0 || noLocation === true
        ? {}
        : pickPositionKeys({
            country_id,
            first_level_id,
            second_level_id,
            third_level_id,
            fourth_level_id,
          });

    const body =
      month && year
        ? { ids, year, month, type, ...positionKeys }
        : { ids, type, ...positionKeys };

    const URL = `/api/v1/stats/data/content/?page=${page}&pageSize=${pageSize}`;
    const {
      data: { rows: data, count },
    } = await Service.post(URL, body);

    setEntry(content, { data, count });
  };

  const createOneContent = async ({ data, content, position }) => {
    const URL = `/api/v1/admin/${routeFor(content)}/create`;
    const { message, status } = await Service.post(URL, data);
    if (status === 200) await fetchDoSeeData({ content, ...position }, 1, 50);
    return { message, status };
  };

  const updateOneContent = async ({ data_id, data, content, position }) => {
    const URL = `/api/v1/admin/${routeFor(content)}/update/${data_id}`;
    const { message, status } = await Service.put(URL, data);
    if (status === 200 && position) {
      await fetchDoSeeData({ content, ...position }, 1, 50);
    }
    return { message, status };
  };

  const deleteOneContent = async ({ data_id, content }) => {
    const URL = `/api/v1/admin/${routeFor(content)}/delete/${data_id}`;
    const { message, status } = await Service.delete(URL);
    return { message, status };
  };

  const updateOneContentStatus = async ({ id, statusState, type }) => {
    const URL = `/api/v1/search_content/status/?id=${id}&status=${statusState}&type=${type}`;
    const { message, status } = await Service.put(URL, {});
    return { message, status };
  };

  const fetchSponsorships = async () => {
    const { data } = await Service.get(`/api/v1/sponsorship`);
    setSponsorships({ data: data?.data, count: data?.count });
  };

  // Exposed for legacy callers that want to hand-seed a list entry.
  const setDataState = ({ data, content, count }) =>
    setEntry(content, { data, count });

  /* ── Legacy projection ───────────────────────────────────────────────── */
  // Derive the old "events / eventsCount / attractions / …" 60-key shape
  // from the registry so consumers keep working. Adding an entity above
  // automatically adds the right keys here — no second place to update.

  const projected = useMemo(() => {
    const out = {};
    for (const e of ENTITIES) {
      const entry = entries[e.type];
      out[e.dataKey] = entry?.data ?? null;
      out[e.countKey] = entry?.count ?? null;
    }
    return out;
  }, [entries]);

  const value = useMemo(
    () => ({
      entries,
      ...projected,
      sponsorships,
      setSponsorships,
      fetchSponsorships,
      fetchDoSeeData,
      createOneContent,
      updateOneContent,
      deleteOneContent,
      updateOneContentStatus,
      setDataState,
    }),
    // fetchDoSeeData and friends close over the current sysAdmin session via
    // Service (which reads the token from localStorage on each call). The
    // memo depends on the admin payload so downstream consumers re-read
    // freshly on login/logout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entries, projected, sponsorships, sysAdmin, singleAdmin],
  );

  return (
    <DoSeeDataContext.Provider value={value}>
      {children}
    </DoSeeDataContext.Provider>
  );
};

/* ── Hooks ───────────────────────────────────────────────────────────────── */

// Legacy hook — returns the same 60-key shape old pages expect.
export const useDoSeeDataContext = () => {
  const ctx = useContext(DoSeeDataContext);
  if (!ctx) {
    throw new Error(
      "useDoSeeDataContext must be used inside <DoSeeDataProvider />",
    );
  }
  return ctx;
};

// Modern hook — per-entity access, no name gymnastics.
//   const { data, count, fetch, create, update, remove, setStatus } = useDoSeeData(_TYPES_.EVENT);
export const useDoSeeData = (type) => {
  const ctx = useContext(DoSeeDataContext);
  if (!ctx) {
    throw new Error("useDoSeeData must be used inside <DoSeeDataProvider />");
  }
  const entry = ctx.entries[type] ?? { data: null, count: null };
  return {
    data: entry.data,
    count: entry.count,
    fetch: (args = {}, page, size) =>
      ctx.fetchDoSeeData({ ...args, content: type }, page, size),
    create: (args = {}) =>
      ctx.createOneContent({ ...args, content: type }),
    update: (args = {}) =>
      ctx.updateOneContent({ ...args, content: type }),
    remove: (args = {}) =>
      ctx.deleteOneContent({ ...args, content: type }),
    setStatus: (args = {}) =>
      ctx.updateOneContentStatus({ ...args, type }),
  };
};
